import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink],
  template: `
    <main class="login-premium-page">
      <section class="login-premium-shell">
        <header class="login-premium-topbar glass-card">
          <div class="brand-area">
            <div class="brand-logo"><img src="/favicon.png" alt="The Gherkin Forge" /></div>
            <div class="brand-copy">
              <div class="brand-title">The Gherkin Forge</div>
              <div class="brand-subtitle">Activation and password reset</div>
            </div>
          </div>

          <div class="hero-actions">
            <a class="secondary-button login-topbar-button" routerLink="/login">Retour a la connexion</a>
          </div>
        </header>

        <section class="login-premium-grid">
          <section class="login-premium-hero">
            <div class="eyebrow">Acces securise</div>
            <h1 class="hero-title">Definissez votre mot de passe.</h1>
            <p class="hero-copy login-hero-copy">
              Cette page vous permet soit d activer un nouveau compte, soit de reinitialiser un mot de passe existant.
              Une fois le mot de passe defini, vous pourrez vous connecter immediatement.
            </p>
          </section>

          <section class="login-premium-panel">
            <section class="login-card panel-card login-card-premium">
              <div class="login-card-header">
                <div>
                  <div class="eyebrow">Activation / Reset</div>
                  <h2 class="subsection-title">Choisir un nouveau mot de passe</h2>
                </div>
                <div class="status-pill status-pill-success">Lien securise</div>
              </div>

              <p class="section-copy login-card-copy">
                Saisissez votre nouveau mot de passe ci-dessous, puis confirmez-le pour finaliser votre acces.
              </p>

              <form class="login-form-premium" [formGroup]="form" (ngSubmit)="submit()" novalidate>
                <label class="field-block login-field-block">
                  <span>Nouveau mot de passe</span>
                  <input type="password" formControlName="password" autocomplete="new-password" placeholder="Au moins 8 caracteres" />
                </label>

                <label class="field-block login-field-block">
                  <span>Confirmer le mot de passe</span>
                  <input type="password" formControlName="confirmPassword" autocomplete="new-password" placeholder="Confirmez votre mot de passe" />
                </label>

                <div class="feedback login-feedback" *ngIf="feedback() as message" [class.feedback--error]="feedbackType() === 'error'">
                  {{ message }}
                </div>

                <button class="primary-button primary-button--wide login-submit-button" type="submit" [disabled]="loading() || success()">
                  {{ loading() ? 'Validation...' : success() ? 'Mot de passe enregistre' : 'Valider et acceder a la connexion' }}
                </button>
              </form>

              <div class="login-divider"></div>

              <div class="login-help-grid">
                <article class="login-help-card">
                  <div class="hero-proof-label">Etape suivante</div>
                  <p>Une fois valide, vous pourrez vous connecter avec votre email ou votre identifiant.</p>
                </article>

                <article class="login-help-card">
                  <div class="hero-proof-label">Lien invalide ?</div>
                  <p>Demandez a votre administrateur de renvoyer un e-mail d activation ou de reset.</p>
                </article>
              </div>
            </section>
          </section>
        </section>
      </section>
    </main>
  `
})
export class ResetPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly loading = signal(false);
  readonly success = signal(false);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'info' | 'error'>('info');
  readonly token = computed(() => (this.route.snapshot.queryParamMap.get('token') || '').trim());

  readonly form = this.fb.nonNullable.group({
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(8)]]
  });

  submit(): void {
    if (!this.token()) {
      this.feedbackType.set('error');
      this.feedback.set('Le lien est invalide ou incomplet.');
      return;
    }

    if (this.form.invalid || this.loading()) {
      this.form.markAllAsTouched();
      this.feedbackType.set('error');
      this.feedback.set('Saisissez un mot de passe d au moins 8 caracteres dans les deux champs.');
      return;
    }

    const { password, confirmPassword } = this.form.getRawValue();
    if (password !== confirmPassword) {
      this.feedbackType.set('error');
      this.feedback.set('La confirmation ne correspond pas au mot de passe saisi.');
      return;
    }

    this.loading.set(true);
    this.feedbackType.set('info');
    this.feedback.set('Validation du mot de passe...');

    this.authService.resetPassword(this.token(), password).subscribe({
      next: () => {
        this.success.set(true);
        this.feedbackType.set('info');
        this.feedback.set('Votre acces est pret. Redirection vers la page de connexion...');
        window.setTimeout(() => {
          void this.router.navigate(['/login']);
        }, 1200);
      },
      error: (error) => {
        this.loading.set(false);
        this.feedbackType.set('error');
        const code = error?.error?.error;
        this.feedback.set(
          code === 'RESET_TOKEN_INVALID'
            ? 'Ce lien de reinitialisation est invalide ou a deja ete utilise.'
            : code === 'PASSWORD_TOO_SHORT'
              ? 'Votre mot de passe doit contenir au moins 8 caracteres.'
              : code === 'ACCOUNT_DISABLED'
                ? 'Ce compte est desactive.'
                : 'Impossible de finaliser cette operation.'
        );
      },
      complete: () => this.loading.set(false)
    });
  }
}
